import { BrandOption, BrandOptions, Thread, Threads } from "../../types"

export const initialMsgNotReplied = (thread: Thread): boolean =>
  thread.messages.length >= 1 &&
  thread.messages.every(message => message.sender === "system")

export const generateBrandOptions = (threads: Threads): BrandOptions => {
  if (!Array.isArray(threads)) {
    return []
  }

  const brandMap = threads.reduce<Record<string, BrandOption>>(
    (acc, thread) => {
      const brand: BrandOption = {
        id: thread.brand_id,
        name: thread.loan_officer.brand_name as string
      }

      // Use a combination of id and name as the key to ensure uniqueness
      const key = brand.id

      if (!acc[key]) {
        acc[key] = brand
      }

      return acc
    },
    {}
  )

  // Convert the brandMap to an array and sort by brand name
  return Object.values(brandMap).sort((a, b) => a.name.localeCompare(b.name))
}

// Function to merge existing threads with new incoming threads
export const mergeThreads = (
  existingThreads: Threads,
  newThreads: Threads
): { merged: Threads; hasNewMessages: boolean } => {
  const existingThreadMap = new Map(
    existingThreads.map(thread => [thread._id, thread])
  )
  let hasNewMessages = false

  if (newThreads.length === 0) {
    return { merged: existingThreads, hasNewMessages: false }
  }

  const mergedThreads = newThreads.map(newThread => {
    const existingThread = existingThreadMap.get(newThread._id)

    if (existingThread) {
      const mergedMessages = mergeMessages(
        existingThread.messages,
        newThread.messages
      )
      const mergedEvents = mergeEvents(existingThread.events, newThread.events)

      const isNewMessage =
        mergedMessages.length > existingThread.messages.length
      if (isNewMessage) {
        hasNewMessages = true
      }

      return {
        ...existingThread,
        ...newThread,
        messages: mergedMessages,
        events: mergedEvents,
        hasNewMessages: isNewMessage
      }
    } else {
      return newThread
    }
  })

  const updatedThreadIds = new Set(newThreads.map(thread => thread._id))
  const removedThreads = existingThreads.filter(
    thread => !updatedThreadIds.has(thread._id)
  )

  return {
    merged: [...mergedThreads, ...removedThreads],
    hasNewMessages
  }
}

// Helper to merge messages
export const mergeMessages = (
  existingMessages: Thread["messages"],
  newMessages: Thread["messages"]
) => {
  const messageMap = new Map(existingMessages.map(msg => [msg._id, msg]))

  // Add or update messages from newMessages
  newMessages.forEach(newMsg => {
    messageMap.set(newMsg._id, newMsg)
  })

  return Array.from(messageMap.values())
}

// Helper to merge events
export const mergeEvents = (
  existingEvents: Thread["events"],
  newEvents: Thread["events"]
) => {
  const eventMap = new Map(existingEvents.map(event => [event._id, event]))

  // Add or update events from newEvents
  newEvents.forEach(newEvent => {
    eventMap.set(newEvent._id, newEvent)
  })

  return Array.from(eventMap.values())
}
